import { usePairs } from "@/features/common/pairs-module";
import { tradingClient } from "@/shared/rpc/clients";
import { adaptiveAmountStore } from "@/shared/stores/AdaptiveAmountStore";
import { bonusStore } from "@/shared/stores/BonusStore";
import { userStore } from "@/shared/stores/UserStore";
import { runInAction } from "mobx";
import {
  getBetPeriodId,
  getBetPeriodPayoutPercent,
  getBetPeriodSeconds,
  getPairBasePayoutPercent,
  getPairBetPeriods,
  getSecondsUntilMarketClose,
} from "@/types/index";
import { trackEvent } from "@/utils/tag-manager";
import { getEstimatedOneWayLatencyMs, getServerNowMs, getServerTimeDelta } from "@/shared/utils/serverTime";
import { AccountType } from "@cronika/proto/common/billing_pb";
import { BetTimeMode, BetType } from "@cronika/proto/common/trading_pb";
import type { TradingPeriodMessage } from "@cronika/proto/common/trading_pb";
import { Buy, Sell } from "@cronika/assets/icons";
import { useTranslation } from "@cronika/locales";
import type { ToastColor } from "@cronika/ui";
import { Button, addToast, cn } from "@cronika/ui";
import { observer } from "mobx-react-lite";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { InsufficientBalanceModal } from "../../../components/InsufficientBalanceModal";
import { MoneySelector } from "../../../components/MoneySelector";
import { TimeSelector } from "../../../components/TimeSelector";
import { EMoneySelectorType, ETimeSelectorType } from "../../../types";

type BetFormProps = {
  onTimePeriodChange?: (period: TradingPeriodMessage | undefined) => void;
  onTimePeriodSecondsChange?: (seconds: number | undefined) => void;
  onButtonHover?: (button: "call" | "put" | null) => void;
};

const showToast = (title: string, description: string, color: ToastColor = "warning", timeout = 7000) => {
  addToast({ title, description, color, timeout });
};

import { Code, ConnectError } from "@connectrpc/connect";
import { mapRpcError, isPayoutConditionsChangedError } from "@/shared/lib/utils/error-mapper";
import { pairsStore } from "@/shared/stores/PairsStore";
import { pendingBetsStore } from "@/shared/stores/PendingBetsStore";
import { tickerStore } from "@/shared/stores/TickerStore";
import { useMediaQuery } from "usehooks-ts";
import { useBetPreferences } from "../../../hooks/useBetPreferences";

export const BetForm: FC<BetFormProps> = observer(
  ({ onTimePeriodChange, onTimePeriodSecondsChange, onButtonHover }) => {
    const { t } = useTranslation("trading");
    const { activePair: selectedPair } = usePairs();
    const {
      betAmount,
      setBetAmount,
      timeItem,
      setTimeItem,
      customDurationSeconds,
      setCustomDurationSeconds,
      loaded: prefsLoaded,
    } = useBetPreferences();
    const [betPeriods, setBetPeriods] = useState<TradingPeriodMessage[]>([]);
    const betPeriodsRef = useRef<TradingPeriodMessage[]>([]);
    betPeriodsRef.current = betPeriods;
    const customDurationSecondsRef = useRef(customDurationSeconds);
    customDurationSecondsRef.current = customDurationSeconds;
    const [showInsufficientModal, setShowInsufficientModal] = useState(false);
    const [timeTargetTs, setTimeTargetTs] = useState<number | null>(null);

    const isHorizontal = useMediaQuery("(max-height: 390px)");

    useEffect(() => {
      if (userStore.currentAccountType === "real") {
        void bonusStore.fetchCurrent();
      }
    }, []);

    useEffect(() => {
      const periods = getPairBetPeriods(selectedPair);
      setBetPeriods(periods);

      const savedCustom = customDurationSecondsRef.current;
      if (savedCustom != null) {
        const exactPreset = periods.find((p) => getBetPeriodSeconds(p) === savedCustom);
        if (exactPreset) {
          setTimeItem(getBetPeriodId(exactPreset));
          setCustomDurationSeconds(null);
          return;
        }

        const minSec = selectedPair?.minDurationSeconds;
        const maxSec = selectedPair?.maxDurationSeconds;
        const belowMin = minSec != null && savedCustom < minSec;
        const aboveMax = maxSec != null && maxSec > 0 && savedCustom > maxSec;
        // Clamp into range instead of nulling out — nulling silently switched the
        // bet to PREDEFINED (whatever period happened to be selected), which the
        // displayed duration (synced to minDurationSeconds, not the period's own
        // TTL) didn't match. Clamping keeps it a CUSTOM bet at the boundary, so
        // what's shown is exactly what gets submitted.
        if (belowMin) setCustomDurationSeconds(minSec);
        else if (aboveMax) setCustomDurationSeconds(maxSec);
      }

      if (periods.length === 0) return;
      setTimeItem((prevTimeItem) => {
        if (!prevTimeItem) return prevTimeItem;
        if (periods.some((p) => getBetPeriodId(p) === prevTimeItem)) return prevTimeItem;
        const prevPeriod = betPeriodsRef.current.find((p) => getBetPeriodId(p) === prevTimeItem);
        if (prevPeriod) {
          const prevSeconds = getBetPeriodSeconds(prevPeriod);
          const match = periods.find((p) => getBetPeriodSeconds(p) === prevSeconds);
          if (match) return getBetPeriodId(match);
        }
        return prevTimeItem;
      });
    }, [selectedPair, prefsLoaded]);

    const selectedBetPeriod = useMemo(() => {
      if (!betPeriods.length) return undefined;
      if (!timeItem) return betPeriods[0];
      return betPeriods.find((period) => getBetPeriodId(period) === timeItem) ?? betPeriods[0];
    }, [betPeriods, timeItem]);

    useEffect(() => {
      onTimePeriodChange?.(selectedBetPeriod);
    }, [selectedBetPeriod, onTimePeriodChange]);

    useEffect(() => {
      const predefinedSeconds = selectedBetPeriod ? getBetPeriodSeconds(selectedBetPeriod) : undefined;
      if (timeTargetTs != null) {
        const serverNowSec = Math.round(getServerNowMs() / 1000);
        onTimePeriodSecondsChange?.(Math.max(timeTargetTs - serverNowSec, 1));
        return;
      }
      onTimePeriodSecondsChange?.(customDurationSeconds ?? predefinedSeconds);
    }, [selectedBetPeriod, customDurationSeconds, timeTargetTs, onTimePeriodSecondsChange]);

    const marketCloseSeconds = getSecondsUntilMarketClose(selectedPair?.schedule);
    const pairWinPercent = getPairBasePayoutPercent(selectedPair);
    const numericPayoutPercent = selectedBetPeriod ? getBetPeriodPayoutPercent(selectedBetPeriod) : pairWinPercent;

    const isZeroBalance = userStore.isZeroBalance;

    const maxBalance = userStore.currentBalance;

    useEffect(() => {
      if (!adaptiveAmountStore.enabled) return;
      if (!betAmount || maxBalance == null || maxBalance <= 0) return;
      const numericAmount = Number(betAmount);
      if (!Number.isNaN(numericAmount) && numericAmount > maxBalance) {
        setBetAmount(String(Math.floor(maxBalance * 100) / 100));
      }
    }, [betAmount, maxBalance, adaptiveAmountStore.enabled]);

    const validateBet = (): { accountType: "demo" | "real"; amount: number } | null => {
      const currentAccountType = userStore.currentAccountType;

      if (!betAmount || Number.isNaN(Number(betAmount)) || !selectedPair) {
        showToast(t("errors.validation.title"), t("errors.validation.invalid-data"));
        return null;
      }

      if (!currentAccountType) {
        showToast(t("errors.validation.title"), t("errors.validation.account-type"));
        return null;
      }

      if (Number(betAmount) < 1) {
        showToast(t("errors.validation.title"), t("errors.validation.minimum-bet"));
        return null;
      }

      const effectiveBalance = Math.max(0, maxBalance ?? 0);
      let amount = Number(betAmount);

      if (amount > effectiveBalance) {
        if (adaptiveAmountStore.enabled && !isZeroBalance && effectiveBalance >= 1) {
          amount = Math.floor(effectiveBalance * 100) / 100;
          setBetAmount(String(amount));
        } else {
          if (currentAccountType === "real") {
            setShowInsufficientModal(true);
          } else {
            showToast(t("errors.validation.title"), t("errors.validation.insufficient-balance"), "danger");
          }
          return null;
        }
      } else if (isZeroBalance) {
        if (currentAccountType === "real") {
          setShowInsufficientModal(true);
        } else {
          showToast(t("errors.validation.title"), t("errors.validation.insufficient-balance"), "danger");
        }
        return null;
      }

      return { accountType: currentAccountType, amount };
    };

    const handleCreateBet = async (valueBetType: BetType) => {
      if (pendingBetsStore.totalActiveBetsCount >= 20) {
        showToast(t("errors.validation.title"), t("errors.validation.max-active-bets"), "warning");
        return;
      }

      const validation = validateBet();
      if (!validation || !selectedPair) return;
      const { accountType: currentAccountType, amount: amt } = validation;

      const isFixedTime = timeTargetTs != null;
      const isCustomDuration = !isFixedTime && customDurationSeconds != null;
      const predefinedSeconds = selectedBetPeriod ? getBetPeriodSeconds(selectedBetPeriod) : 60;
      const serverNowSec = Math.round(getServerNowMs() / 1000);

      const durationSeconds = isFixedTime
        ? Math.max(timeTargetTs - serverNowSec, 1)
        : isCustomDuration
          ? customDurationSeconds
          : predefinedSeconds;

      const maxDur = selectedPair.maxDurationSeconds ?? 0;
      if (maxDur > 0 && durationSeconds > maxDur) {
        showToast(t("errors.validation.title"), t("errors.validation.max-duration"), "warning");
        return;
      }

      const tempId = `optimistic-${crypto.randomUUID()}`;
      const serverNow = getServerNowMs();
      const estimatedTargetTime = serverNow + getEstimatedOneWayLatencyMs() + durationSeconds * 1000;
      pendingBetsStore.add({
        betId: tempId,
        updateType: "created",
        status: "open",
        timestamp: new Date(serverNow).toISOString(),
        createdAt: new Date(serverNow).toISOString(),
        targetTime: new Date(estimatedTargetTime).toISOString(),
        pairSymbol: selectedPair.symbol,
        betType: valueBetType === BetType.PUT ? "put" : "call",
        amount: amt,
        entryPrice: tickerStore.prices.get(selectedPair.symbol) ?? undefined,
        payoutPercent: numericPayoutPercent,
        minRaisePoints: selectedBetPeriod?.minRaisePoints ?? undefined,
      });

      runInAction(() => {
        if (userStore.balance) {
          const bal = userStore.balance;
          if (currentAccountType === "real") {
            if (bonusStore.isActive) {
              userStore.setBalance({
                ...bal,
                bonusBalance: String(Math.max(0, Number(bal.bonusBalance) - amt)),
                tradingBalance: String(Math.max(0, Number(bal.tradingBalance) - amt)),
              });
            } else {
              userStore.setBalance({
                ...bal,
                realBalance: String(Math.max(0, Number(bal.realBalance) - amt)),
                tradingBalance: String(Math.max(0, Number(bal.tradingBalance) - amt)),
              });
            }
          } else {
            userStore.setBalance({
              ...bal,
              demoBalance: String(Math.max(0, Number(bal.demoBalance) - amt)),
            });
          }
        }
      });

      try {
        const timeDelta = getServerTimeDelta();
        if (timeDelta !== null) {
          console.log(
            `[timedelta] server vs client: ${timeDelta > 0 ? "+" : ""}${timeDelta}ms (client is ${timeDelta > 0 ? "behind" : "ahead"} by ${Math.abs(timeDelta)}ms)`,
          );
        } else {
          console.log("[timedelta] server time not yet received");
        }

        const clientEntryPrice = tickerStore.prices.get(selectedPair.symbol);
        const submitBet = (timeMode: BetTimeMode, opts: { seconds?: number; targetTs?: number }) =>
          tradingClient.createBet({
            pairId: selectedPair.id,
            betType: valueBetType,
            accountType: currentAccountType === "real" ? AccountType.REAL : AccountType.DEMO,
            amount: String(amt),
            timeMode,
            durationSeconds: opts.seconds,
            targetTs: opts.targetTs != null ? String(opts.targetTs) : undefined,
            entryPrice: clientEntryPrice != null ? String(clientEntryPrice) : undefined,
            expectedPayoutPercent: String(numericPayoutPercent),
          });

        let createdBet;
        try {
          if (isFixedTime) {
            createdBet = await submitBet(BetTimeMode.FIXED, { targetTs: timeTargetTs });
          } else {
            createdBet = await submitBet(isCustomDuration ? BetTimeMode.CUSTOM : BetTimeMode.PREDEFINED, {
              seconds: durationSeconds,
            });
          }
        } catch (error) {
          const noMatchingRule =
            (isFixedTime || isCustomDuration) && error instanceof ConnectError && error.code === Code.NotFound;
          if (!noMatchingRule) throw error;
          showToast(
            t("errors.create-bet.bad-request.title"),
            t("errors.create-bet.custom-duration-fallback"),
            "warning",
          );
          createdBet = await submitBet(BetTimeMode.PREDEFINED, { seconds: predefinedSeconds });
        }

        pendingBetsStore.update(tempId, createdBet.id, {
          entryPrice: createdBet.entryPrice ? Number(createdBet.entryPrice) : undefined,
          targetTime: createdBet.targetTs || undefined,
          minRaisePoints: createdBet.minRaisePoints ?? undefined,
          payoutPercent: createdBet.payoutPercent ?? undefined,
        });

        if (currentAccountType === "real") {
          trackEvent("trading", "real_trade_open", "real_trade_opened");
        }
      } catch (error) {
        pendingBetsStore.remove(tempId);
        runInAction(() => {
          if (userStore.balance) {
            const bal = userStore.balance;
            if (currentAccountType === "real") {
              if (bonusStore.isActive) {
                userStore.setBalance({
                  ...bal,
                  bonusBalance: String(Number(bal.bonusBalance) + amt),
                  tradingBalance: String(Number(bal.tradingBalance) + amt),
                });
              } else {
                userStore.setBalance({
                  ...bal,
                  realBalance: String(Number(bal.realBalance) + amt),
                  tradingBalance: String(Number(bal.tradingBalance) + amt),
                });
              }
            } else {
              userStore.setBalance({
                ...bal,
                demoBalance: String(Number(bal.demoBalance) + amt),
              });
            }
          }
        });
        console.error("Create bet error:", error);

        if (isPayoutConditionsChangedError(error)) {
          void pairsStore.fetchPairs(true);
          return;
        }

        const { key, params } =
          error instanceof ConnectError
            ? mapRpcError(error)
            : { key: "errors.create-bet.bad-request.description" as const, params: undefined };
        showToast(t("errors.create-bet.bad-request.title"), t(key as any, params), "danger");
      }
    };

    const potentialWin = useMemo(() => {
      const amount = Number(betAmount);
      if (!betAmount || Number.isNaN(amount)) return "0.00";
      return (amount * (1 + numericPayoutPercent / 100)).toFixed(2);
    }, [betAmount, numericPayoutPercent]);

    return (
      <>
        <InsufficientBalanceModal
          isOpen={showInsufficientModal}
          onClose={() => setShowInsufficientModal(false)}
          balance={maxBalance ?? 0}
          betAmount={Number(betAmount)}
        />
        <div className="rounded-2xl w-full z-10">
          <div>
            <div>
              <TimeSelector
                betPeriods={betPeriods}
                onChange={setTimeItem}
                value={timeItem}
                type={ETimeSelectorType.DROPDOWN}
                marketCloseSeconds={marketCloseSeconds}
                maxDurationSeconds={selectedPair?.maxDurationSeconds}
                minDurationSeconds={selectedPair?.minDurationSeconds}
                onCustomDurationChange={setCustomDurationSeconds}
                onTimeTargetChange={setTimeTargetTs}
                initialCustomDurationSeconds={customDurationSeconds}
              />
            </div>
            <div className={isHorizontal ? "mt-2" : "mt-2"}>
              <MoneySelector
                defaultAmount="20"
                value={betAmount}
                onAmountChange={setBetAmount}
                type={EMoneySelectorType.DROPDOWN}
                maxBalance={maxBalance}
                minAmount={bonusStore.minTrade}
                maxAmount={bonusStore.maxTrade}
              />
            </div>
          </div>

          {isHorizontal ? (
            <div
              data-tour-target="return"
              className="flex justify-between items-center bg-surface-dark rounded-lg p-1.5 px-4.25 mt-2 h-10"
            >
              <span className="inline text-default-500 text-[12px]">{t("earnings")}:</span>
              <div className="text-right flex flex-row justify-center items-center gap-1">
                <span className="inline text-success font-semibold text-[12px] leading-4">
                  +{numericPayoutPercent}%
                </span>
                <p className="text-[12px] leading-4 font-bold">${potentialWin}</p>
              </div>
            </div>
          ) : (
            <div
              data-tour-target="return"
              className="flex flex-col items-center justify-center gap-0.5 bg-default-500/15 rounded-lg p-1.5 px-4.25 mt-2 text-center"
            >
              <p className="text-default-500 text-[12px]">
                {t("profitability")}: +{numericPayoutPercent}%
              </p>
              <p className="text-success text-[12px] font-semibold">
                {t("profit")}: +${potentialWin}
              </p>
            </div>
          )}

          <div
            data-tour-target="direction"
            className={cn("flex", isHorizontal ? "flex-row h-10 gap-2 mt-2" : "flex-col gap-2 mt-2")}
          >
            <Button
              data-tour-target="direction-call"
              className={cn("w-full text-white text-base font-bold cursor-pointer", isHorizontal && "w-26 h-10")}
              size="lg"
              radius="lg"
              color="success"
              disabled={!selectedPair || marketCloseSeconds === 0}
              onClick={() => handleCreateBet(BetType.CALL)}
              onMouseEnter={() => onButtonHover?.("call")}
              onMouseLeave={() => onButtonHover?.(null)}
              endContent={!isHorizontal && <Buy className="text-white ml-2" />}
            >
              {t("call-btn").toUpperCase()}
            </Button>
            <Button
              data-tour-target="direction-put"
              className={cn("w-full text-white text-base font-bold cursor-pointer", isHorizontal && "w-26 h-10")}
              size="lg"
              radius="lg"
              color="danger"
              disabled={!selectedPair || marketCloseSeconds === 0}
              onClick={() => handleCreateBet(BetType.PUT)}
              onMouseEnter={() => onButtonHover?.("put")}
              onMouseLeave={() => onButtonHover?.(null)}
              endContent={!isHorizontal && <Sell className="text-white ml-2" />}
            >
              {t("put-btn").toUpperCase()}
            </Button>
          </div>
        </div>
      </>
    );
  },
);
